import { apiFetch, type ApiError } from "./client";

export type GateStatus = "AUTO_PASS" | "REVIEW" | "CONFIRMED" | "REJECTED";
export type ImportStatus = "PROCESSING" | "NEEDS_REVIEW" | "READY" | "FINALIZED" | "FAILED";
export type CandidateType = "product";

/**
 * One catalog candidate from the persisted JSONB payload. v2 rows carry
 * { id, type, sku, name, sim }; older rows carry { productId, sku, name, sim }
 * — use candidateTarget to read both.
 */
export type ImportCandidate = {
  id?: string | null;
  type?: CandidateType;
  /** Older payload key (always a product). */
  productId?: string | null;
  sku: string;
  name: string;
  sim: number;
};

/** Normalize a v1/v2 candidate to its resolve target; null for malformed rows. */
export function candidateTarget(c: ImportCandidate): { id: string; type: CandidateType } | null {
  const id = c.id ?? c.productId;
  if (!id) return null;
  return { id, type: c.type ?? "product" };
}

export type ImportItem = {
  id: string;
  position: number;
  rawName: string;
  quantity: number | null;
  unit: string | null;
  packaging: string | null;
  rawCode: string | null;
  contextNote: string | null;
  duplicate: boolean;
  note: string | null;
  matchedProductId: string | null;
  matchedSku: string | null;
  matchedName: string | null;
  matchConfidence: "high" | "medium" | "low" | null;
  attributeCheck: Record<string, string> | null;
  candidates: ImportCandidate[] | null;
  matchReason: string | null;
  escalated: boolean;
  violations: string[];
  gateStatus: GateStatus;
  resolvedProductId: string | null;
  manuallyAdded: boolean;
  resolvedQuantity: number | null;
};

export type ImportStage = "EXTRACTING" | "ANALYZING" | "MATCHING" | "GATING" | "DONE" | "FAILED";

/** Short-lived presigned link to the originally uploaded source document. */
export type ImportDocument = {
  url: string;
  filename: string;
  contentType: string;
};

export type OrderImport = {
  id: string;
  companyId: string;
  originalFilename: string;
  status: ImportStatus;
  stage: ImportStage | null;
  progressDone: number;
  progressTotal: number;
  extractionConfidence: string | null;
  scanned: boolean;
  ambiguities: string[] | null;
  errorMessage: string | null;
  orderId: string | null;
  createdAt: string;
  items: ImportItem[];
};

async function uploadMultipart(companyId: string, file: File): Promise<{ id: string; status: string }> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/orders/imports?companyId=${encodeURIComponent(companyId)}`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    let body: { code?: string; message?: string } = {};
    try { body = (await res.json()) as { code?: string; message?: string }; } catch { /* ignore */ }
    throw { status: res.status, code: body.code ?? `HTTP_${res.status}`, message: body.message ?? res.statusText };
  }
  return res.json() as Promise<{ id: string; status: string }>;
}

export const importsApi = {
  upload: (companyId: string, file: File) => uploadMultipart(companyId, file),
  get: (id: string) => apiFetch<OrderImport>(`/orders/imports/${id}`),
  list: (companyId: string) => apiFetch<OrderImport[]>(`/orders/imports?companyId=${encodeURIComponent(companyId)}`),
  /** Presigned link (short TTL) to the originally uploaded source document. */
  document: (id: string) => apiFetch<ImportDocument>(`/orders/imports/${id}/document`),
  /** Reverse lookup: the import that produced an order. 404 resolves to null, not a throw. */
  byOrder: async (orderId: string): Promise<OrderImport | null> => {
    try {
      return await apiFetch<OrderImport>(`/orders/imports/by-order/${orderId}`);
    } catch (err) {
      if ((err as ApiError).status === 404) return null;
      throw err;
    }
  },
  /** Operator appends a row the AI missed. */
  addItem: (
    id: string,
    req: { productId?: string; quantity: number; note?: string },
  ) => apiFetch<ImportItem>(`/orders/imports/${id}/items`, { method: "POST", body: req }),
  resolve: (
    id: string,
    itemId: string,
    req: {
      action: "CONFIRM" | "SELECT" | "REJECT";
      // SELECT requires productId (the backend now resolves products only).
      productId?: string;
      quantity?: number;
    },
  ) => apiFetch<void>(`/orders/imports/${id}/items/${itemId}/resolve`, { method: "POST", body: req }),
  finalize: (id: string) => apiFetch<{ orderId: string }>(`/orders/imports/${id}/finalize`, { method: "POST" }),
};
